import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SendToOption } from '@shared/models/send-to-option';
import { ISectionDirtyCheckService } from '@shared/models/section-dirty-check';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { DentureDetailsDirtyCheckService } from '@modules/denture-details/denture-details-dirty-check.service';
import { RxIntuitiveDentureDirtyCheckService } from '@modules/rx-intuitive-denture-procedure/rx-intuitive-denture-dirty-check.service';
import { ApplianceDetailsDirtyCheckService } from '@modules/appliance-details/appliance-details-dirty-check.service';
import { RxIntuitiveApplianceDirtyCheckService } from '@modules/rx-intuitive-appliance-details/rx-intuitive-appliance-dirty-check.service';
import { SurgicalGuideDirtyCheckService } from '@modules/rx-intuitive-scan-surgical-guide/surgical-guide-dirty-check.service';
import { NotesDirtyCheckService } from '@modules/notes/notes-dirty-check.service';
import { ProcedureTypeV1DirtyCheckService } from '@modules/order/procedure-type-v1-dirty-check.service';
import { DueDateDirtyCheckService } from '@modules/order/due-date-dirty-check.service';
import { AttachmentsDirtyCheckService } from '@shared/rx-attachments/attachments-dirty-check.service';
import { ProcedureMap } from '@shared/models/procedure-map';

export type RxFlow = 'v1' | 'intuitive';

/**
 * Orchestrator for Send To template-change confirmations.
 * Resolves the target template once and fans out to all section dirty-check services.
 * Provided in AppComponent (not root).
 */
@Injectable({ providedIn: 'root' })
export class RxTemplateDirtyCheckService {
	private readonly v1Services: ISectionDirtyCheckService[];
	private readonly intuitiveServices: ISectionDirtyCheckService[];
	private readonly sharedServices: ISectionDirtyCheckService[];

	constructor(
		private readonly rxTemplateService: RxTemplateService,
		private readonly dentureV1: DentureDetailsDirtyCheckService,
		private readonly dentureIntuitive: RxIntuitiveDentureDirtyCheckService,
		private readonly applianceV1: ApplianceDetailsDirtyCheckService,
		private readonly applianceIntuitive: RxIntuitiveApplianceDirtyCheckService,
		private readonly procedureTypeV1: ProcedureTypeV1DirtyCheckService,
		private readonly surgicalGuide: SurgicalGuideDirtyCheckService,
		private readonly notes: NotesDirtyCheckService,
		private readonly dueDate: DueDateDirtyCheckService,
		private readonly attachments: AttachmentsDirtyCheckService
	) {
		this.v1Services = [this.dentureV1, this.applianceV1, this.procedureTypeV1];
		this.intuitiveServices = [this.dentureIntuitive, this.applianceIntuitive, this.surgicalGuide];
		this.sharedServices = [this.notes, this.dueDate, this.attachments];
	}

	shouldConfirmFor$(sendTo: SendToOption | null, targetProcedureMap: ProcedureMap | null, flow: RxFlow): Observable<boolean> {
		const targetTemplate = this.rxTemplateService.resolveTemplate(sendTo);
		const flowServices = flow === 'v1' ? this.v1Services : this.intuitiveServices;
		const allServices = [...flowServices, ...this.sharedServices];

		return combineLatest(allServices.map(s => s.willLoseDataFor$(targetTemplate, targetProcedureMap))).pipe(
			take(1),
			map(results => results.some(Boolean))
		);
	}
}
